import { UI, HStack, Stack, VStack } from '@genial/components';
import { BoxRadius, Spacing } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import React, { useEffect } from 'react';

import { InputMoney } from '../../../../../../../../../components/InputMoney';
import { ResponsiveSuccessIconButton } from '../../../../../../../../../components/ResponsiveSuccessIconButton';
import { RiskRating } from '../../../../../../../../../components/RiskRating';
import { useCoesAssetController } from '../../../../../hooks/products/Coes';
import { useCoesBillingTypeController } from '../../../../../hooks/products/Coes/billing';
import { AssetTitleWithSubtitle } from '../../../components/AssetTitleWithSubtitle';
import { AssetValueWithVariation } from '../../../components/AssetValueWithVariation';
import { LateralBar } from '../../../components/LateralBar';

import { CoesItemSelectedTypes } from '.';

export const CoesItemSelectedMobile: React.FC<CoesItemSelectedTypes.Props> = ({
  offer,
  onUpdate,
  hideLateralBar = false,
}) => {
  const id = offer.productIdNormalized;
  const {
    control,
    handleInputBlur,
    handleInputClear,
    handleInputFocus,
    handleSubmit,
    hasError,
    helpText,
    isFocused,
    currentValueFormatted,
    currentValue,
  } = useCoesAssetController(offer);

  const {
    billingTypeSelected,
    onChangeBillingTypeSelected,
    billingTypeOptions,
    applicationIn,
    isLoading,
  } = useCoesBillingTypeController({ product: offer });

  useEffect(() => {
    if (offer.orderValue === currentValue || !currentValue) {
      return;
    }
    const updatedOffer = { ...offer, orderValue: currentValue || 0 };
    onUpdate(updatedOffer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue]);

  useEffect(() => {
    if (offer.billingTypeId === billingTypeSelected || !billingTypeSelected) {
      return;
    }
    const updatedOffer = { ...offer, billingTypeId: billingTypeSelected };
    onUpdate(updatedOffer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingTypeSelected]);

  const middleInfos = [
    {
      label: 'Risco',
      value: (
        <RiskRating
          currentRiskRating={offer.riskRatingScoreNormalized}
          tooltip={offer.riskRatingNormalized}
        />
      ),
    },
    {
      label: 'Vencimento',
      value: <UI.Text color="neutral.dark">{offer.maturityDateNormalized}</UI.Text>,
    },
    ...offer.additionalInfoNormalized.map((info) => ({
      label: info.labelNormalized,
      value: <AssetValueWithVariation value={info.valueNormalized} variation={info.variation} />,
    })),
    {
      label: 'Aplicação em',
      value: (
        <UI.Text color="neutral.dark" isLoading={isLoading} w="100px">
          {applicationIn || offer.applicationIn}
        </UI.Text>
      ),
    },
    {
      label: 'Aplicação mínima',
      value: (
        <UI.Text color="neutral.dark">
          {Utils.Mask.Money(offer.minimumApplicationNormalized)}
        </UI.Text>
      ),
    },
  ];

  return (
    <VStack space="stack.xxxs" w="full">
      {!hideLateralBar && (
        <LateralBar
          backgroundColor="product.estruturados"
          borderTopLeftRadius={BoxRadius.sm}
          borderBottomLeftRadius={BoxRadius.sm}
        />
      )}
      <VStack space="stack.nano">
        <AssetTitleWithSubtitle
          title={offer.productNameNormalized}
          subtitle={offer.strategyNormalized}
        />
      </VStack>
      <VStack space="stack.nano" flexGrow={1}>
        {middleInfos.map(({ label, value }) => (
          <HStack space="inline.quarck" justifyContent="space-between" key={label}>
            <UI.Text color="neutral.black" variant="semibold">
              {label}
            </UI.Text>
            {value}
          </HStack>
        ))}
      </VStack>
      <VStack space={Spacing['stack.quarck']} flexGrow={1}>
        <UI.Text color="neutral.black" variant="semibold">
          Tipo de boletagem
        </UI.Text>
        <Stack flex={1}>
          <UI.Select
            testID="select-billing-option"
            triggerProps={{
              isDisabled: isLoading || billingTypeOptions.length === 0,
              _disabled: { opacity: 0.2 },
            }}
            options={billingTypeOptions}
            onValueChange={onChangeBillingTypeSelected}
            selectedValue={billingTypeSelected}
            placeholder="Selecione"
          />
        </Stack>
      </VStack>
      <HStack
        space={Spacing['inline.xxxs']}
        justifyContent="space-between"
        w="full"
        alignItems="flex-end">
        <VStack space={Spacing['stack.quarck']} flexGrow={1}>
          <UI.Text color="neutral.black" variant="semibold">
            Valor aplicado
          </UI.Text>
          <VStack flex={1}>
            <InputMoney
              id={`coes-input${id}`}
              name="createAppliedAmount"
              control={control}
              handleFocus={handleInputFocus}
              handleBlur={handleInputBlur}
              handleSubmit={handleSubmit}
              handleClear={handleInputClear}
              currentValue={currentValueFormatted}
              hasFocus={isFocused}
              minValue={offer.minimumApplication}
              customInputProps={{
                ...{ ...(isFocused && hasError && { error: hasError }) },
                ...{ ...(isFocused && !hasError && { helpText }) },
              }}
            />
          </VStack>
        </VStack>
        <ResponsiveSuccessIconButton />
      </HStack>
    </VStack>
  );
};
